const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * Formats a date string to MM/DD/YYYY, h:mm AM/PM format
 * @param dateString - ISO date string or any valid date string
 * @returns Formatted date string with time in user's local timezone, or fallback value for invalid dates
 */
export const formatDateTime = ( dateString: string | null | undefined ): string => {
	if ( !dateString ) {
		return '—'
	}

	const date = new Date( dateString )

	// Check if date is valid
	if ( isNaN( date.getTime() ) ) {
		return '—'
	}

	return date.toLocaleString( 'en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	} )
}

/**
 * Formats a date string to MM/DD/YYYY format (date only, no time)
 * @param dateString - ISO date string or any valid date string
 * @returns Formatted date string without time, or fallback value for invalid dates
 */
export const formatDate = ( dateString: string | null | undefined ): string => {
	if ( !dateString ) {
		return '—'
	}

	// For ISO date strings (YYYY-MM-DD), parse as local date to avoid timezone issues
	if ( ISO_DATE_PATTERN.test( dateString.trim() ) ) {
		const [ yearStr, monthStr, dayStr ] = dateString.trim().split( '-' )
		const year = parseInt( yearStr, 10 )
		const month = parseInt( monthStr, 10 )
		const day = parseInt( dayStr, 10 )

		// Validate date components
		if ( month < 1 || month > 12 || day < 1 || day > 31 ) {
			return '—'
		}

		const date = new Date( year, month - 1, day )

		// Check if date is valid and matches the input (handles invalid dates like Feb 31)
		if ( isNaN( date.getTime() ) || date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year ) {
			return '—'
		}

		return date.toLocaleDateString( 'en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric'
		} )
	}

	// For other date formats, use standard parsing
	const date = new Date( dateString )

	// Check if date is valid
	if ( isNaN( date.getTime() ) ) {
		return '—'
	}

	return date.toLocaleDateString( 'en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric'
	} )
}

/**
 * Formats a date string to M/D/YYYY 12:00:00 AM format for Shopify/Ratelinx integration.
 * Month and day are not zero-padded. Time is hardcoded to 12:00:00 AM.
 * Only call with YYYY-MM-DD strings — the non-ISO fallback reads local timezone and
 * will shift the date for users behind UTC.
 */
export const formatShopifyDate = ( dateString: string | null | undefined ): string => {
	if ( !dateString ) {
		return ''
	}

	if ( ISO_DATE_PATTERN.test( dateString.trim() ) ) {
		const [ yearStr, monthStr, dayStr ] = dateString.trim().split( '-' )
		const year = parseInt( yearStr, 10 )
		const month = parseInt( monthStr, 10 )
		const day = parseInt( dayStr, 10 )

		if ( month < 1 || month > 12 || day < 1 || day > 31 ) {
			return ''
		}

		const date = new Date( year, month - 1, day )

		if ( isNaN( date.getTime() ) || date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year ) {
			return ''
		}

		return `${month}/${day}/${year} 12:00:00 AM`
	}

	const date = new Date( dateString )

	if ( isNaN( date.getTime() ) ) {
		return ''
	}

	return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} 12:00:00 AM`
}
